import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { createProjectImage, deleteProjectImage, getProjectImages } from './projectImagesSlice'

const initialState = {
	entities: {},
	ids: [],
	recents: [],
	isLoading: false,
	isInitialized: false,
}

function prepConfig(getState) {
	const {
		userLogin: { userInfo },
	} = getState()

	return {
		headers: {
			Authorization: `Bearer ${userInfo.token}`,
		},
	}
}

export const initializeProjects = createAsyncThunk(
	'projects/initialize',
	async (payload: any, { getState, rejectWithValue }) => {
		const { locationId } = payload

		try {
			const { data } = await axios.get(`/api/projects/${locationId}/location`, prepConfig(getState))
			return data
		} catch (error) {
			console.error('Failed to initialize projects:', error)
			return rejectWithValue('Failed to initialize projects')
		}
	}
)

export const initializeAdminProjects = createAsyncThunk(
	'projects/initializeAdmin',
	async (_, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(`/api/projects/admin`, prepConfig(getState))
			return data
		} catch (error) {
			console.error('Failed to initialize admin projects:', error)
			return rejectWithValue('Failed to initialize projects')
		}
	}
)
export const initializeMyProjects = createAsyncThunk(
	'projects/initializeMyProjects',
	async (_, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(`/api/projects/myProjects`, prepConfig(getState))
			return data
		} catch (error) {
			console.error('Failed to initialize my projects:', error)
			return rejectWithValue('Failed to initialize projects')
		}
	}
)

export const createProject = createAsyncThunk(
	'projects/create',
	async (project: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post(`/api/projects`, project, prepConfig(getState))
			return data
		} catch (error: any) {
			console.error('Failed to create project:', error)
			return rejectWithValue(error?.response?.data?.message || 'Failed to create project')
		}
	}
)

export const deleteProject = createAsyncThunk(
	'projects/delete',
	async (payload: any, { getState, rejectWithValue }) => {
		const { projectId } = payload

		try {
			const { data } = await axios.put(`/api/projects/${projectId}/delete`, {}, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to delete project')
		}
	}
)

export const patchProject = createAsyncThunk('projects/patch', async (payload: any, { getState, rejectWithValue }) => {
	const { projectId, updates } = payload

	try {
		const { data } = await axios.patch(`/api/projects/${projectId}/patch`, updates, prepConfig(getState))
		return data
	} catch (error) {
		console.error(error)
		return rejectWithValue('Unable to patch project')
	}
})

export const updateProject = createAsyncThunk(
	'projects/update',
	async (payload: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.put(`/api/projects/${payload._id}`, payload, prepConfig(getState))
			return data
		} catch (error) {
			console.error('Failed to update project:', error)
			return rejectWithValue('Failed to update project')
		}
	}
)

export const addPartnerToProject = createAsyncThunk(
	'projects/addPartner',
	async (payload: any, { getState, rejectWithValue }) => {
		const { projectId, partner } = payload

		console.log(`payload: `, payload)

		// debugger

		try {
			const { data } = await axios.post(
				`/api/projects/${projectId}/addpartners`,
				{ partnerId: partner._id, partnerUserId: partner.partner._id, projectId },
				prepConfig(getState)
			)
			return data
		} catch (error) {
			return rejectWithValue('Failed to add partner')
		}
	}
)

export const addUpdateNoteToProject = createAsyncThunk(
	'projects/addUpdateNote',
	async (payload: any, { getState, rejectWithValue }) => {
		const { projectId, updateNote } = payload

		try {
			const { data } = await axios.post(
				`/api/projects/${projectId}/updateNote`,
				{ updateNote, projectId },
				prepConfig(getState)
			)
			return data
		} catch (error) {
			return rejectWithValue('Failed to add update note')
		}
	}
)

export const removePartnerFromProject = createAsyncThunk(
	'projects/removePartner',
	async (payload: any, { getState, rejectWithValue }) => {
		const { projectId, partnerId } = payload

		try {
			const { data } = await axios.post(
				`/api/projects/${projectId}/removePartner`,
				{ projectId: projectId, partnerId: partnerId },
				prepConfig(getState)
			)
			return data
		} catch (error) {
			return rejectWithValue('Failed to remove partner')
		}
	}
)

export const getUpdatedProject = createAsyncThunk(
	'projects/getUpdate',
	async (projectId, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(`/api/projects/${projectId}`, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to update project')
		}
	}
)

// Extract the key out of the entitiy object
// eslint-disable-next-line no-unused-vars
const removeKey = (key, { [key]: _, ...rest }) => rest

const projectSlice = createSlice({
	name: 'projects',
	initialState,
	reducers: {},
	extraReducers: {
		[initializeProjects.fulfilled as any]: (state, action) => {
			const projects = action.payload

			// Filter out invalid entries and sort by updatedAt in reverse chronological order (most recent first)
			const validProjects = (projects || []).filter(p => p && p._id)
			const sortedProjects = [...validProjects].sort((a, b) => {
				const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime()
				const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime()
				return dateB - dateA
			})

			state.recents = []
			state.ids = sortedProjects.map((loc, index) => {
				state.entities[loc._id] = loc

				if (index < 4) {
					state.recents.push(loc._id)
				}
				return loc._id
			})
			state.isLoading = false
			state.isInitialized = true
		},
		[initializeProjects.pending as any]: state => {
			state.isLoading = true
		},
		[initializeProjects.rejected as any]: (state, action) => {
			state.isLoading = false
			state.isInitialized = true
			state.error = action.error.message
		},
		[initializeMyProjects.fulfilled as any]: (state, action) => {
			const projects = action.payload

			// Filter out invalid entries and sort by updatedAt in reverse chronological order (most recent first)
			const validProjects = (projects || []).filter(p => p && p._id)
			const sortedProjects = [...validProjects].sort((a, b) => {
				const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime()
				const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime()
				return dateB - dateA
			})

			state.recents = []
			state.ids = sortedProjects.map((loc, index) => {
				state.entities[loc._id] = loc

				if (index < 4) {
					state.recents.push(loc._id)
				}
				return loc._id
			})
			state.isLoading = false
			state.isInitialized = true
		},
		[initializeMyProjects.pending as any]: state => {
			state.isLoading = true
		},
		[initializeMyProjects.rejected as any]: (state, action) => {
			state.isLoading = false
			state.isInitialized = true
			state.error = action.error.message
		},
		[initializeAdminProjects.fulfilled as any]: (state, action) => {
			const projects = action.payload

			// Filter out invalid entries and sort by updatedAt in reverse chronological order (most recent first)
			const validProjects = (projects || []).filter(p => p && p._id)
			const sortedProjects = [...validProjects].sort((a, b) => {
				const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime()
				const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime()
				return dateB - dateA
			})

			state.recents = []
			state.ids = sortedProjects.map((loc, index) => {
				state.entities[loc._id] = loc

				if (index < 4) {
					state.recents.push(loc._id)
				}
				return loc._id
			})
			state.isLoading = false
			state.isInitialized = true
		},
		[initializeAdminProjects.pending as any]: state => {
			state.isLoading = true
		},
		[initializeAdminProjects.rejected as any]: (state, action) => {
			state.isLoading = false
			state.isInitialized = true
			state.error = action.error.message
		},
		[createProject.fulfilled as any]: (state, action) => {
			const project = action.payload
			state.entities[project._id] = project
			state.ids.push(project._id)
			state.recents.unshift(project._id)

			if (state.recents.length > 4) {
				state.recents.pop()
			}
		},
		[updateProject.fulfilled as any]: (state, action) => {
			const project = action.payload
			const projectId = project._id
			state.entities[projectId] = project
		},
		[deleteProject.fulfilled as any]: (state, action) => {
			const id = action.meta.arg
			state.entities = removeKey(id, state.entities)
			state.ids = state.ids.filter(stateId => stateId !== id)
		},
		[patchProject.fulfilled as any]: (state, action) => {
			const project = action.payload
			state.entities[project._id] = { ...action.payload, ...state.entities[project._id] }
			state.isInitialized = false
		},

		[getUpdatedProject.fulfilled as any]: (state, action) => {
			// works for any asset type since all types are stored under entities
			state.entities[action.meta.arg] = { ...action.payload, ...state.entities[action.meta.arg] }
			state.isLoading = false
		},
		[getUpdatedProject.rejected as any]: (state, action) => {
			state.error = action.error
		},
		[addPartnerToProject.fulfilled as any]: (state, action) => {
			// The server returns the project with its partners populated (MYD-589).
			// Merge only the partners: the stored copy may carry more populated
			// relations (projectAsset, updateHistory) than this response does.
			const project = action.payload
			const existing = state.entities[project._id]
			state.entities[project._id] = existing ? { ...existing, partners: project.partners } : project
			state.isInitialized = false
		},
		[addPartnerToProject.rejected as any]: (state, action) => {
			console.log(action)
		},
		// The getProjectNotes / createProjectNote handlers that lived here were
		// removed with projectNoteSlice (DT-023). They could never fire: nothing
		// dispatched those thunks, and the endpoint they called
		// (/api/projectnotes/*) is not mounted on the server. Project notes are
		// served by the location notes screen via locationNoteSlice and
		// /api/locationnotes.
		[getProjectImages.fulfilled as any]: (state, action) => {
			const images = action.payload
			const locationId = action.meta.arg
			const locationRef = state.entities[locationId]
			if (locationRef && images?.length) {
				const locationImageIds = images.map(n => n._id)
				locationRef.images = locationImageIds
			}
		},
		[createProjectImage.fulfilled as any]: (state, action) => {
			const image = action.payload
			const locationId = image.projectid
			const locationRef = state.entities[locationId]
			if (locationRef && image) {
				if (locationRef.images) {
					locationRef.images.push(image._id)
				} else {
					locationRef.images = [image._id]
				}
			}
		},
		[deleteProjectImage.fulfilled as any]: (state, action) => {
			const locationId = action.payload.projectid
			const imageId = action.meta.arg
			const updatedImageIds = state.entities[locationId]?.images?.filter((imgId: any) => {
				return imgId !== imageId
			})
			state.entities[locationId].images = updatedImageIds
		},
		[removePartnerFromProject.fulfilled as any]: (state, action) => {
			const partners = action.payload
			state.entities[action.meta.arg.projectId].partners = partners
		},
		[addUpdateNoteToProject.fulfilled as any]: (state, action) => {
			const project = action.payload
			state.entities[project._id] = project
			state.isInitialized = false
		},
		[addUpdateNoteToProject.rejected as any]: (state, action) => {
			console.log(action)
		},
	} as any,
})

export default projectSlice
